import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
// THE red-folder no-trade window rule, not a copy of it. Lives beside the other
// calendar rules in news-service so there is one definition of "60 before,
// 5 after, high only, overlaps unioned"; `@news` aliases news-service/src.
import { newsRestrictionWindows, RestrictionWindow } from '@news/calendar';

export interface CalendarLine {
  time: number; // timestamp in ms
  event: string; // event name
  country: string; // country name
  importance: 'high' | 'medium' | 'low';
  isPast: boolean; // event has passed
}

interface CalendarLineRendererData {
  x: number;
  time: number;
  event: string;
  country: string;
  importance: 'high' | 'medium' | 'low';
  isPast: boolean;
}

// Colors for importance levels
const IMPORTANCE_COLORS = {
  high: { line: 'rgba(239, 68, 68, 0.7)', star: '#ef4444' },    // Red
  medium: { line: 'rgba(249, 115, 22, 0.6)', star: '#f97316' }, // Orange
  low: { line: 'rgba(34, 197, 94, 0.5)', star: '#22c55e' },     // Green
};

/**
 * Red-folder no-trade band.
 *
 * Deliberately BELOW the alpha at which the band competes with price: this is a
 * warning, not a redaction, and a band you cannot read candles through would be
 * turned off within a day.
 *
 * The elapsed fill is the part of the window that is already behind `now`. It is
 * dimmer because it carries no decision — the bright part is the part you still
 * have to survive. There is no border at the `now` seam, only at the two real
 * boundaries, so the only hard edges on screen are the ones the rule actually
 * has.
 *
 * THESE ALPHAS ARE NOT WHAT LANDS ON SCREEN. lightweight-charts calls a
 * zOrder-'bottom' renderer TWICE per repaint — verified against the dashed
 * event line below, which has always been stroked twice per frame, and which is
 * why nobody noticed: strokes and text are idempotent, alpha fills are not. So
 * `upcoming` composites to roughly 0.19, not 0.10. They are left at the honest
 * single-pass value rather than pre-halved, because if the library ever settles
 * on one call the band gets fainter, and fainter-than-intended is the safe
 * direction to fail for a warning; pre-halved values would double instead.
 */
const RESTRICTION_COLORS = {
  upcoming: 'rgba(239, 68, 68, 0.10)',
  elapsed: 'rgba(239, 68, 68, 0.035)',
  edge: 'rgba(239, 68, 68, 0.30)',
};

export interface NewsRestrictionRendererData {
  /** Left edge in chart pixels, already clamped to the canvas. */
  x1: number;
  /** Right edge in chart pixels, already clamped to the canvas. */
  x2: number;
  /** x of `now` when it falls strictly inside the window, else null. */
  xSplit: number | null;
  /**
   * The whole window is behind `now`, so there is nothing left to act on.
   *
   * Not the same as `xSplit === null`: a live window whose remaining minutes
   * sit past the right edge of the viewport has no seam to draw either, and it
   * still has to be painted at full strength.
   */
  fullyElapsed: boolean;
  /** The real window start is on screen (so its border is not a clamp artifact). */
  startVisible: boolean;
  /** The real window end is on screen. */
  endVisible: boolean;
}

/**
 * Just the 2D-context surface this renderer touches.
 *
 * Spelled out instead of reaching for the DOM global: eslint's config for this
 * project does not declare browser globals, so naming `CanvasRenderingContext2D`
 * in a type position is a `no-undef` error. The line renderer below has carried
 * that error since it was written; there is no reason to add a second one.
 */
interface BandContext {
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  fillRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
}

class NewsRestrictionPaneRenderer implements IPrimitivePaneRenderer {
  private _data: NewsRestrictionRendererData[] = [];

  update(data: NewsRestrictionRendererData[]): void {
    this._data = data;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as BandContext;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const height = scope.bitmapSize.height as number;

      for (const w of this._data) {
        const left = w.x1 * pixelRatio;
        const right = w.x2 * pixelRatio;
        // Each band is painted from a UNIONED, disjoint interval and the two
        // sub-rects below never overlap each other, so nothing ever double-fills
        // — overlapping events cannot compound into a darker region.
        if (w.xSplit !== null) {
          const split = w.xSplit * pixelRatio;
          ctx.fillStyle = RESTRICTION_COLORS.elapsed;
          ctx.fillRect(left, 0, split - left, height);
          ctx.fillStyle = RESTRICTION_COLORS.upcoming;
          ctx.fillRect(split, 0, right - split, height);
        } else {
          ctx.fillStyle = w.fullyElapsed
            ? RESTRICTION_COLORS.elapsed
            : RESTRICTION_COLORS.upcoming;
          ctx.fillRect(left, 0, right - left, height);
        }

        // Boundaries, drawn only where they are the real thing. A window that
        // runs off the side of the chart must not grow a fake edge there.
        ctx.strokeStyle = RESTRICTION_COLORS.edge;
        ctx.lineWidth = 1 * pixelRatio;
        ctx.beginPath();
        if (w.startVisible) {
          ctx.moveTo(left, 0);
          ctx.lineTo(left, height);
        }
        if (w.endVisible) {
          ctx.moveTo(right, 0);
          ctx.lineTo(right, height);
        }
        ctx.stroke();
      }
    });
  }
}

class NewsRestrictionPaneView implements IPrimitivePaneView {
  private _source: CalendarLinePrimitive;
  private _renderer: NewsRestrictionPaneRenderer;

  constructor(source: CalendarLinePrimitive) {
    this._source = source;
    this._renderer = new NewsRestrictionPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom'; // Behind price. A band over the candles would be a redaction.
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRestrictionRendererData());
    return this._renderer;
  }
}

class CalendarLinePaneRenderer implements IPrimitivePaneRenderer {
  private _data: CalendarLineRendererData[] = [];
  private _bottomOffset: number = 45;

  update(data: CalendarLineRendererData[], bottomOffset: number): void {
    this._data = data;
    this._bottomOffset = bottomOffset;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const bitmapHeight = scope.bitmapSize.height as number;

      // Position stars at bottom, same area as other markers
      const markerY = bitmapHeight - (this._bottomOffset * pixelRatio);

      for (const line of this._data) {
        const x = line.x * pixelRatio;
        const colors = IMPORTANCE_COLORS[line.importance];

        // Dim past events
        const alpha = line.isPast ? 0.35 : 1.0;
        ctx.globalAlpha = alpha;

        // Draw vertical dashed line
        ctx.beginPath();
        ctx.strokeStyle = colors.line;
        ctx.lineWidth = 1 * pixelRatio;
        ctx.setLineDash([4 * pixelRatio, 4 * pixelRatio]);
        ctx.moveTo(x, 0);
        ctx.lineTo(x, markerY - 12 * pixelRatio);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw star marker at bottom (click to see details)
        ctx.font = `${14 * pixelRatio}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⭐', x, markerY);

        // Draw time below star
        ctx.font = `${8 * pixelRatio}px Arial`;
        ctx.fillStyle = line.isPast ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.5)';
        ctx.textBaseline = 'top';
        const timeStr = this.formatTime(line.time);
        ctx.fillText(timeStr, x, markerY + 10 * pixelRatio);
      }

      ctx.globalAlpha = 1.0;
    });
  }

  private formatTime(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles'
    }).toLowerCase();
  }
}

class CalendarLinePaneView implements IPrimitivePaneView {
  private _source: CalendarLinePrimitive;
  private _renderer: CalendarLinePaneRenderer;

  constructor(source: CalendarLinePrimitive) {
    this._source = source;
    this._renderer = new CalendarLinePaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom'; // Draw behind candles
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.getBottomOffset());
    return this._renderer;
  }
}

export class CalendarLinePrimitive implements ISeriesPrimitive<Time> {
  private _lines: CalendarLine[] = [];
  private _paneView: CalendarLinePaneView;
  private _restrictionPaneView: NewsRestrictionPaneView;
  private _requestUpdate?: () => void;
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _bottomOffset: number = 45; // Same as other markers
  private _rendererData: CalendarLineRendererData[] = [];

  constructor() {
    this._paneView = new CalendarLinePaneView(this);
    this._restrictionPaneView = new NewsRestrictionPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    // Band FIRST. Both views are zOrder 'bottom', and within one primitive the
    // views paint in array order, so this is what keeps the existing dashed
    // event line drawing OVER the band instead of being washed out by it.
    return [this._restrictionPaneView, this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setLines(lines: CalendarLine[]): void {
    this._lines = lines;
    this._requestUpdate?.();
  }

  setFontSize(_size: number): void {
    // Deprecated - kept for backwards compatibility
    this._requestUpdate?.();
  }

  getBottomOffset(): number {
    return this._bottomOffset;
  }

  /**
   * Check if a click hits any star marker
   * Returns the line if hit, null otherwise
   */
  checkClick(clickX: number, clickY: number, chartHeight: number): CalendarLine | null {
    const hitRadius = 25; // pixels
    const markerY = chartHeight - this._bottomOffset;

    for (let i = 0; i < this._rendererData.length; i++) {
      const data = this._rendererData[i];
      const dx = Math.abs(clickX - data.x);
      const dy = Math.abs(clickY - markerY);

      if (dx <= hitRadius && dy <= hitRadius) {
        return this._lines[i];
      }
    }

    return null;
  }

  /**
   * The red-folder no-trade windows for the lines currently set.
   *
   * Same feed as the vertical lines — `setLines` is the only input — so the
   * band cannot drift out of step with the star it belongs to, and it picks up
   * socket updates without any extra wiring.
   *
   * Importance is re-filtered here rather than trusted from upstream: the
   * chart's event feed honours the user's importance filter, which can include
   * medium and low, and only red folder gets a band.
   */
  getRestrictionWindows(): RestrictionWindow[] {
    return newsRestrictionWindows(
      this._lines.map(l => ({ timestamp: l.time, importance: l.importance }))
    );
  }

  /**
   * Map an epoch-ms instant to a chart x coordinate.
   *
   * `timeToCoordinate` only answers for instants that exist in the series, and a
   * window edge is an arbitrary instant — 60 minutes back lands mid-bar, 5
   * minutes forward is often past the last candle entirely. The linear fallback
   * over the visible range is the same one `FVGZonePrimitive` uses for spans
   * that run off the right edge.
   */
  private _timeToX(
    ms: number,
    chartWidth: number,
    visibleRange: { from: unknown; to: unknown } | null
  ): number | null {
    if (!this._chart) return null;
    const timeScale = this._chart.timeScale();
    const seconds = Math.floor(ms / 1000);
    const coord = timeScale.timeToCoordinate(seconds as Time);
    if (coord !== null) return coord as number;

    if (!visibleRange || chartWidth <= 0) return null;
    const from = visibleRange.from as number;
    const to = visibleRange.to as number;
    const span = to - from;
    if (span <= 0) return null;

    // Anchor the extrapolation on where the first and last visible BARS actually
    // sit, instead of assuming they sit on the two edges of the canvas. They do
    // not — lightweight-charts leaves blank space to the right of the last bar,
    // so the naive version stretches the same seconds across a wider box and
    // pushes every future instant too far right. For this band that is the
    // difference between "the restriction ends at 11:05" and a fill jammed flat
    // against the edge of the chart with no end in sight.
    const xFrom = timeScale.timeToCoordinate(from as Time);
    const xTo = timeScale.timeToCoordinate(to as Time);
    if (xFrom !== null && xTo !== null && (xTo as number) > (xFrom as number)) {
      const pxPerSecond = ((xTo as number) - (xFrom as number)) / span;
      return (xFrom as number) + (seconds - from) * pxPerSecond;
    }

    return ((seconds - from) / span) * chartWidth;
  }

  getRestrictionRendererData(): NewsRestrictionRendererData[] {
    if (!this._chart) return [];

    const timeScale = this._chart.timeScale();
    const chartWidth = timeScale.width();
    if (chartWidth <= 0) return [];
    const visibleRange = timeScale.getVisibleRange();

    const now = Date.now();
    const data: NewsRestrictionRendererData[] = [];

    for (const w of this.getRestrictionWindows()) {
      const rawStart = this._timeToX(w.start, chartWidth, visibleRange);
      const rawEnd = this._timeToX(w.end, chartWidth, visibleRange);
      if (rawStart === null || rawEnd === null) continue;

      // Entirely off screen — nothing to paint, and clamping it would smear a
      // 1px stripe onto the edge of the chart.
      if (rawEnd < 0 || rawStart > chartWidth) continue;

      const x1 = Math.max(0, rawStart);
      const x2 = Math.min(chartWidth, rawEnd);
      if (x2 - x1 < 0.5) continue;

      let xSplit: number | null = null;
      if (now > w.start && now < w.end) {
        const rawNow = this._timeToX(now, chartWidth, visibleRange);
        if (rawNow !== null) {
          const clamped = Math.min(x2, Math.max(x1, rawNow));
          // Only worth a seam if both sides are actually wide enough to see.
          if (clamped - x1 >= 1 && x2 - clamped >= 1) xSplit = clamped;
        }
      }

      data.push({
        x1,
        x2,
        xSplit,
        fullyElapsed: now >= w.end,
        startVisible: rawStart >= 0 && rawStart <= chartWidth,
        endVisible: rawEnd >= 0 && rawEnd <= chartWidth,
      });
    }

    return data;
  }

  getRendererData(): CalendarLineRendererData[] {
    if (!this._chart) return [];

    const timeScale = this._chart.timeScale();
    const data: CalendarLineRendererData[] = [];

    // Get visible range and chart width for extrapolating future coordinates
    const visibleRange = timeScale.getVisibleRange();
    const chartWidth = this._chart.timeScale().width();

    for (const line of this._lines) {
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(line.time / 1000) as Time;

      // Get x coordinate from time
      let x = timeScale.timeToCoordinate(timeInSeconds);

      // If timeToCoordinate returns null (future time without data),
      // calculate the position manually using linear extrapolation
      if (x === null && visibleRange && chartWidth > 0) {
        const rangeStart = visibleRange.from as number;
        const rangeEnd = visibleRange.to as number;
        const timeRange = rangeEnd - rangeStart;

        if (timeRange > 0) {
          // Calculate position based on where this time falls relative to visible range
          const relativePosition = ((timeInSeconds as number) - rangeStart) / timeRange;
          x = (relativePosition * chartWidth) as any; // Cast to Coordinate

          // Only include if it would be visible (within chart bounds + some padding)
          if (x !== null && (x < -50 || x > chartWidth + 200)) {
            continue;
          }
        }
      }

      if (x === null) continue;

      data.push({
        x,
        time: line.time,
        event: line.event,
        country: line.country,
        importance: line.importance,
        isPast: line.isPast,
      });
    }

    // Cache for hit testing
    this._rendererData = data;

    return data;
  }
}
